import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Plus, Edit, Trash2, Store, ExternalLink, Map, List, MapPin, Search, X } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";

// Fix Leaflet default marker icons (broken with webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  name: "",
  address: "",
  phone: "",
  website: "",
  country: "",
  notes: "",
};

function hasValidCoords(o) {
  if (!o.lat || !o.lng) return false;
  const lat = parseFloat(o.lat);
  const lng = parseFloat(o.lng);
  return !isNaN(lat) && !isNaN(lng) && !(lat === 0 && lng === 0);
}

function FitBounds({ outlets }) {
  const map = useMap();
  useEffect(() => {
    const pinned = outlets.filter(hasValidCoords);
    if (pinned.length === 0) return;
    if (pinned.length === 1) {
      map.setView([parseFloat(pinned[0].lat), parseFloat(pinned[0].lng)], 10);
    } else {
      const bounds = L.latLngBounds(pinned.map((o) => [parseFloat(o.lat), parseFloat(o.lng)]));
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [outlets, map]);
  return null;
}

async function nominatimGeocode(address, country) {
  const query = [address, country].filter(Boolean).join(", ");
  if (!query.trim()) return null;
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`,
      { headers: { "User-Agent": "TSPM-RetailOutlets/1.0" } }
    );
    if (res.status === 429) return "rate_limited";
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.length) return null;
    const lat = parseFloat(data[0].lat);
    const lng = parseFloat(data[0].lon);
    if (lat === 0 && lng === 0) return null;
    return { lat: String(lat), lng: String(lng) };
  } catch {
    return null;
  }
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

export default function RetailOutlets() {
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("table");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeProgress, setGeocodeProgress] = useState({ done: 0, total: 0 });
  const geocodeCancelRef = useRef(false);
  const [search, setSearch] = useState("");
  const [showMissingOnly, setShowMissingOnly] = useState(false);

  const fetchOutlets = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/retail-outlets`, { withCredentials: true });
      setOutlets(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load retail outlets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOutlets(); }, []);

  const openAdd = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (outlet) => {
    setEditTarget(outlet);
    setForm({
      name: outlet.name,
      address: outlet.address,
      phone: outlet.phone,
      website: outlet.website,
      country: outlet.country,
      notes: outlet.notes,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editTarget) {
        await axios.put(`${API}/retail-outlets/${editTarget.id}`, form, { withCredentials: true });
        toast.success("Outlet updated");
      } else {
        await axios.post(`${API}/retail-outlets`, form, { withCredentials: true });
        toast.success("Outlet added");
      }
      setDialogOpen(false);
      fetchOutlets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save outlet");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/retail-outlets/${deleteTarget.id}`, { withCredentials: true });
      toast.success("Outlet deleted");
      setDeleteTarget(null);
      fetchOutlets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete outlet");
    } finally {
      setDeleting(false);
    }
  };

  // Batch-geocode outlets missing coordinates, directly from browser
  const handleGeocodeAll = async () => {
    const missing = outlets.filter((o) => !hasValidCoords(o) && (o.address || o.country));
    if (missing.length === 0) { toast.success("All outlets already have coordinates"); return; }
    geocodeCancelRef.current = false;
    setGeocoding(true);
    setGeocodeProgress({ done: 0, total: missing.length });
    let succeeded = 0;
    let delay = 1200;
    for (let i = 0; i < missing.length; i++) {
      if (geocodeCancelRef.current) break;
      const outlet = missing[i];
      const geo = await nominatimGeocode(outlet.address, outlet.country);
      if (geo === "rate_limited") {
        // Back off and retry this outlet
        toast.error("Rate limited — waiting 15s before retrying…", { id: "geocode-rl" });
        await sleep(15000);
        delay = Math.min(delay + 500, 3000); // slow down going forward
        i--; // retry same outlet
        continue;
      }
      if (geo) {
        try {
          await axios.patch(
            `${API}/retail-outlets/${outlet.id || '_'}/coords`,
            { lat: geo.lat, lng: geo.lng, _row: outlet._row },
            { withCredentials: true }
          );
          succeeded++;
        } catch { /* skip failed rows */ }
      }
      setGeocodeProgress({ done: i + 1, total: missing.length });
      if (i < missing.length - 1) await sleep(delay);
    }
    setGeocoding(false);
    toast.success(`Geocoded ${succeeded} of ${missing.length} outlets`);
    fetchOutlets();
  };

  const field = (key, label, opts = {}) => (
    <div>
      <Label className="text-xs uppercase tracking-widest text-gray-500 mb-1 block">{label}</Label>
      {opts.textarea ? (
        <Textarea
          value={form[key]}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          placeholder={opts.placeholder || ""}
          rows={3}
          className="rounded-none"
        />
      ) : (
        <Input
          value={form[key]}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          placeholder={opts.placeholder || ""}
          className="rounded-none"
        />
      )}
    </div>
  );

  const filteredOutlets = outlets.filter((o) => {
    if (showMissingOnly && hasValidCoords(o)) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      o.name?.toLowerCase().includes(q) ||
      o.address?.toLowerCase().includes(q) ||
      o.country?.toLowerCase().includes(q) ||
      o.phone?.toLowerCase().includes(q) ||
      o.notes?.toLowerCase().includes(q)
    );
  });

  const pinnedOutlets = filteredOutlets.filter(hasValidCoords);
  const missingCount = outlets.filter((o) => !hasValidCoords(o) && (o.address || o.country)).length;

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Retail Outlets</h1>
          <p className="text-sm text-gray-500 mt-1">
            {search ? `${filteredOutlets.length} of ${outlets.length}` : outlets.length} outlet{outlets.length !== 1 ? "s" : ""} · {pinnedOutlets.length} on map
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {missingCount > 0 && !geocoding && (
            <Button
              onClick={handleGeocodeAll}
              variant="outline"
              className="rounded-none gap-2 text-xs"
            >
              <MapPin size={13} />
              Geocode {missingCount} missing
            </Button>
          )}
          {geocoding && (
            <div className="flex items-center gap-2 text-xs text-gray-500 border border-gray-200 px-3 py-2">
              <div className="w-3 h-3 border border-black border-t-transparent rounded-full animate-spin" />
              {geocodeProgress.done}/{geocodeProgress.total} geocoded
              <button
                onClick={() => { geocodeCancelRef.current = true; }}
                className="text-gray-400 hover:text-black ml-1"
              >
                Stop
              </button>
            </div>
          )}
          {/* View toggle */}
          <div className="border border-gray-200 flex">
            <button
              onClick={() => setView("table")}
              className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors flex items-center gap-1.5 ${view === "table" ? "bg-black text-white" : "text-gray-500 hover:bg-gray-50"}`}
            >
              <List size={13} /> Table
            </button>
            <button
              onClick={() => setView("map")}
              className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors flex items-center gap-1.5 border-l border-gray-200 ${view === "map" ? "bg-black text-white" : "text-gray-500 hover:bg-gray-50"}`}
            >
              <Map size={13} /> Map
            </button>
          </div>
          <Button onClick={openAdd} className="rounded-none bg-black text-white hover:bg-gray-800 gap-2">
            <Plus size={16} />
            Add Outlet
          </Button>
        </div>
      </div>

      {/* Search bar */}
      <div className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, address, country, phone..."
            className="w-full border border-gray-200 pl-9 pr-8 py-2 text-sm focus:outline-none focus:border-black"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black">
              <X size={13} />
            </button>
          )}
        </div>
        <button
          onClick={() => setShowMissingOnly((v) => !v)}
          className={`px-3 py-2 text-xs uppercase tracking-widest border transition-colors whitespace-nowrap flex items-center gap-1.5 ${
            showMissingOnly ? "bg-black text-white border-black" : "border-gray-200 text-gray-500 hover:border-black hover:text-black"
          }`}
        >
          <MapPin size={12} />
          Missing coords {missingCount > 0 && `(${missingCount})`}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
        </div>
      ) : view === "map" ? (
        /* ── Map View ── */
        <div className="border border-gray-200">
          {pinnedOutlets.length === 0 ? (
            <div className="p-12 text-center">
              <Map size={32} className="mx-auto text-gray-300 mb-3" />
              <p className="text-sm uppercase tracking-widest text-gray-400">No geocoded outlets yet</p>
              {missingCount > 0 && (
                <Button onClick={handleGeocodeAll} variant="outline" className="rounded-none mt-4 gap-2">
                  <MapPin size={14} />
                  Geocode {missingCount} outlets
                </Button>
              )}
            </div>
          ) : (
            <MapContainer
              center={[20, 0]}
              zoom={2}
              style={{ height: "600px", width: "100%" }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds outlets={pinnedOutlets} />
              {pinnedOutlets.map((outlet) => (
                <Marker
                  key={outlet.id}
                  position={[parseFloat(outlet.lat), parseFloat(outlet.lng)]}
                >
                  <Popup>
                    <div className="min-w-[180px]">
                      <p className="font-bold text-sm mb-1">{outlet.name}</p>
                      {outlet.address && <p className="text-xs text-gray-600 mb-0.5">{outlet.address}</p>}
                      {outlet.country && <p className="text-xs text-gray-600 mb-0.5">{outlet.country}</p>}
                      {outlet.phone && <p className="text-xs text-gray-600 mb-0.5">{outlet.phone}</p>}
                      {outlet.website && (
                        <a href={/^https?:\/\//i.test(outlet.website) ? outlet.website : `https://${outlet.website}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1">
                          <ExternalLink size={10} /> Website
                        </a>
                      )}
                      <div className="flex gap-2 mt-2 pt-2 border-t border-gray-100">
                        <button onClick={() => openEdit(outlet)} className="text-xs text-gray-500 hover:text-black flex items-center gap-1">
                          <Edit size={10} /> Edit
                        </button>
                        <button onClick={() => setDeleteTarget(outlet)} className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1">
                          <Trash2 size={10} /> Delete
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
          {outlets.length > pinnedOutlets.length && (
            <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-400 flex items-center justify-between">
              <span>{outlets.length - pinnedOutlets.length} outlet{outlets.length - pinnedOutlets.length !== 1 ? "s" : ""} not on map</span>
              {missingCount > 0 && !geocoding && (
                <button onClick={handleGeocodeAll} className="text-black hover:underline flex items-center gap-1">
                  <MapPin size={11} /> Geocode missing
                </button>
              )}
            </div>
          )}
        </div>
      ) : outlets.length === 0 ? (
        /* ── Empty state ── */
        <div className="border border-gray-200 p-12 text-center">
          <Store size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm uppercase tracking-widest text-gray-400">No outlets yet</p>
          <Button onClick={openAdd} variant="outline" className="rounded-none mt-4 gap-2">
            <Plus size={14} />
            Add your first outlet
          </Button>
        </div>
      ) : (
        /* ── Table View ── */
        <div className="border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-gray-500 font-medium">Name</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-gray-500 font-medium hidden md:table-cell">Address</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-gray-500 font-medium hidden lg:table-cell">Country</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-gray-500 font-medium hidden lg:table-cell">Phone</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-gray-500 font-medium hidden xl:table-cell">Website</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-gray-500 font-medium hidden xl:table-cell">Map</th>
                <th className="px-4 py-3 w-20" />
              </tr>
            </thead>
            <tbody>
              {filteredOutlets.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">No outlets match "{search}"</td></tr>
              ) : filteredOutlets.map((outlet, i) => (
                <tr key={outlet.id} className={`border-b border-gray-100 hover:bg-gray-50 ${i % 2 === 0 ? "" : "bg-gray-50/50"}`}>
                  <td className="px-4 py-3 font-medium">
                    <div>{outlet.name}</div>
                    {outlet.notes && <div className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{outlet.notes}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{outlet.address}</td>
                  <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">{outlet.country}</td>
                  <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">{outlet.phone}</td>
                  <td className="px-4 py-3 hidden xl:table-cell">
                    {outlet.website ? (
                      <a href={/^https?:\/\//i.test(outlet.website) ? outlet.website : `https://${outlet.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-black hover:underline text-xs">
                        <ExternalLink size={12} />
                        {outlet.website.replace(/^https?:\/\//i, "").split("/")[0]}
                      </a>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 hidden xl:table-cell">
                    {hasValidCoords(outlet) ? (
                      <span className="text-xs text-green-600">Pinned</span>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => openEdit(outlet)} className="p-1.5 text-gray-400 hover:text-black transition-colors" title="Edit">
                        <Edit size={14} />
                      </button>
                      <button onClick={() => setDeleteTarget(outlet)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors" title="Delete">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editTarget ? "Edit Outlet" : "Add Retail Outlet"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {field("name", "Name *", { placeholder: "Store name" })}
            {field("address", "Address", { placeholder: "Street address" })}
            <div className="grid grid-cols-2 gap-3">
              {field("country", "Country", { placeholder: "e.g. UK" })}
              {field("phone", "Phone", { placeholder: "+44 ..." })}
            </div>
            {field("website", "Website", { placeholder: "https://..." })}
            {field("notes", "Notes", { textarea: true, placeholder: "Any additional info" })}
          </div>
          <p className="text-xs text-gray-400 mt-1">Coordinates are auto-generated from the address.</p>
          <div className="flex gap-3 mt-4">
            <Button onClick={handleSave} disabled={saving} className="flex-1 rounded-none bg-black text-white hover:bg-gray-800">
              {saving ? "Saving…" : editTarget ? "Save Changes" : "Add Outlet"}
            </Button>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none" disabled={saving}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="rounded-none max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete Outlet</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 mt-2">
            Delete <span className="font-medium">{deleteTarget?.name}</span>? This will permanently remove it from the spreadsheet.
          </p>
          <div className="flex gap-3 mt-4">
            <Button onClick={handleDelete} disabled={deleting} className="flex-1 rounded-none bg-red-600 text-white hover:bg-red-700">
              {deleting ? "Deleting…" : "Delete"}
            </Button>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="rounded-none" disabled={deleting}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
